import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  Button,
  TextField,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import EmojiPicker from './EmojiPicker';
import {
  getCheckDuplicateSubcategory,
  getCheckDuplicateCategory,
  postCategoryForm,
  postSubcategoryForm,
} from '../../../../api/category';
import { AwaitButton } from '../../../../reusable-components/await-button';

EditCategory.propTypes = {
  row: PropTypes.object,
  categories: PropTypes.array,
  fetchData: PropTypes.func,
};

export default function EditCategory({ row, categories, fetchData }) {
  const [open, setOpen] = useState(false);
  const [icon, setIcon] = useState(row.icon);

  const { enqueueSnackbar } = useSnackbar();

  const [category, setCategory] = useState(null);
  const [name, setName] = useState('');

  const handleClickOpen = () => {
    setOpen(true);
    setCategory(row?.mainCategoryId ? categories.find((c) => c.id === row.mainCategoryId) : null);
    setName(row.name);
  };
  const handleClose = () => {
    setOpen(false);
    setCategory(null);
    setName('');
  };

  const handleUpdate = async () => {
    if (!name) {
      enqueueSnackbar('Name is required', 'error');
      return;
    }
    try {
      if (row.mainCategoryId) {
        const duplicate = await getCheckDuplicateSubcategory(row.subId, row.mainCategoryId, name);
        if (duplicate.data) {
          enqueueSnackbar('Subcategory already exists', { variant: 'error' });
          return;
        }
        const res = await postSubcategoryForm({ id: row.subId, name, mainCategoryId: row.mainCategoryId });
        if (res.data) enqueueSnackbar(`Subcategory updated`, { variant: 'success' });
        else enqueueSnackbar(`Subcategory update failed`, { variant: 'error' });
      } else {
        const duplicate = await getCheckDuplicateCategory(row.id, name);
        if (duplicate.data) {
          enqueueSnackbar('Category already exists', { variant: 'error' });
          return;
        }
        const res = await postCategoryForm({ id: row.id, name, icon });
        if (res.data) enqueueSnackbar(`Category updated`, { variant: 'success' });
        else enqueueSnackbar(`Category update failed`, { variant: 'error' });
      }
    } catch {
      enqueueSnackbar(`Something went wrong`, { variant: 'error' });
    }
    fetchData();
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<EditIcon />}
        sx={{ mr: 2 }}
        onClick={handleClickOpen}
        color="secondary"
      >
        Edit
      </Button>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          Edit {row?.mainCategoryId ? 'Subcategory' : 'Category'}
         <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent sx={{ height: '100%', width: '100%' }}>
          {row.mainCategoryId ? (
            <>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Change Category</DialogContentText>
              <Autocomplete
                sx={{
                  mt: 1,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': 'none',
                }}
                size="small"
                fullWidth
                type="new-password"
                options={categories}
                autoHighlight
                getOptionLabel={(option) => option?.name}
                value={category}
                onChange={(event, newValue) => {
                  setCategory(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                    }}
                    autoComplete="off"
                  />
                )}
              />{' '}
            </>
          ) : (
            <>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Change Icon</DialogContentText>
              <EmojiPicker icon={icon} setIcon={setIcon} categories={categories}/>
            </>
          )}
          <>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Change Name</DialogContentText>{' '}
            <TextField
              value={name || ''}
              onChange={(e) => setName(e.target.value)}
              label=""
              size="small"
              variant="outlined"
              sx={{ width: '100%' }}
              autoComplete="off"
            />
          </>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton
            variant="contained"
            color="primary"
            size="medium"
            sx={{ width: 150, mr: 2 }}
            onClick={handleUpdate}
            disabled={!name.trim()}
          >
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
